import { useCallback, useEffect, useRef, useState } from 'react'
import { getRankedNearbyCameras, getRankedNearbyCamerasByImagePixels } from '@utils/CameraRankingUtils'
import { ShareButtonsGroup } from '@/features/shopping'

interface Camera {
  intrinsics: { width: number; height: number }
  position: [number, number, number]
  frame_file: string
  [key: string]: any
}

interface TapCameraIndicatorProps {
  cameraCount: number
  position: { x: number; y: number }
  
  /**
   * Current projection position in this frame (frame-relative coordinates).
   * This is where the circle currently renders and can change when navigating between frames.
   */
  currentDisplayX: number
  currentDisplayY: number
  
  // Shop ID for generating shareable links
  shopId: string
  
  /**
   * Original tap position (frame-relative coordinates, IMMUTABLE).
   * These coordinates represent where the user originally tapped relative to the frame's top-left.
   * Used for ALL discovery operations to ensure consistent 3D point lookup.
   * 
   * CRITICAL: These coordinates are frame-relative (e.g., 255.3px from frame edge),
   * NOT screen coordinates (e.g., 277px from viewport edge).
   */
  frameRelativeTapX: number
  frameRelativeTapY: number
  
  /**
   * Image pixel coordinates at tap time (IMMUTABLE, ZOOM-INDEPENDENT).
   * These are pre-calculated at tap time and stored here.
   * They represent the exact pixel location in the image's coordinate system (0-450, 0-800).
   * Used for camera discovery to avoid any container dimension dependency.
   * 
   * CRITICAL: These are ZOOM-INDEPENDENT and don't change even if zoom changes later.
   * This ensures discovery always looks up the correct 3D point and cameras.
   */
  imagePixelX: number
  imagePixelY: number
  
  frameCamera: Camera
  frameIndex: number
  cameras: Camera[]
  points3D: any[]
  offsetMap: any | null
  
  // Frame dimensions for circle sizing
  frameDisplayWidth?: number
  
  // Radius in image space pixels (will be scaled to display size)
  radiusPxImageSpace?: number
  
  // 3D sphere projection scale data (per-camera radius calculation)
  scaleData?: {
    circleScale: number
    distanceScale: number
    circleRadiusPx: number
    depth: number
    focalLength: number
  }
  
  // Zoom scale for correct touch offset calculation
  zoomScale: number
  
  // Callback when thumbnails should show
  onShowThumbnails: (cameras: any[], radiusPx?: number, selectedFrameIndex?: number) => void
  
  // Callback to update radius without regenerating camera list
  onUpdateRadius?: (radiusPx: number) => void
  
  // Callback when knob drag status changes (to pause/resume parent gestures)
  onDraggingStatusChange?: (isDragging: boolean) => void
  
  // Callback when camera badge is clicked (to trigger playback)
  onCameraBadgeClick?: () => void
  
  // Callback when disk panning completes - passes final screen position and unscaled display position
  onPanComplete?: (finalScreenX: number, finalScreenY: number, finalDisplayX: number, finalDisplayY: number) => void

  // Preview mode: show only blue circle (used for shared item URLs)
  // In preview mode, only the blue circle renders. Tapping it calls onActivate to switch to full mode.
  previewMode?: boolean

  // Called when user taps the circle in preview mode to activate full controls
  onActivate?: () => void

  // Called when user closes the indicator
  onClose?: () => void
}

/**
 * TapCameraIndicator - Shows a circle at tap location with second-tap discovery
 *
 * Features:
 * - Displays red circle at projected 3D point
 * - Shows camera count in the circle
 * - Second tap triggers camera discovery and shows nearby camera thumbnails
 */
export function TapCameraIndicator({
  cameraCount,
  position,
  currentDisplayX,
  currentDisplayY,
  frameRelativeTapX,
  frameRelativeTapY,
  shopId,
  imagePixelX,
  imagePixelY,
  frameCamera,
  frameIndex,
  cameras,
  points3D,
  offsetMap,
  frameDisplayWidth,
  radiusPxImageSpace,
  scaleData,
  zoomScale,
  onShowThumbnails,
  onUpdateRadius,
  onDraggingStatusChange,
  onCameraBadgeClick,
  onPanComplete,
  onClose,
  previewMode,
  onActivate,
}: TapCameraIndicatorProps) {
  // Show thumbnails (used by second tap, pan complete, and radius updates)
  const showThumbnails = useCallback(() => {
    console.log('📸 [TapCameraIndicator] Showing camera thumbnails')
    
    // Get ranked nearby cameras using IMAGE PIXEL coordinates
    const rankedCameras = getRankedNearbyCamerasByImagePixels(
      imagePixelX,
      imagePixelY,
      frameCamera,
      cameras,
      points3D,
      offsetMap,
      frameIndex,
      5 // Max 5 thumbnails
    )
    
    // Convert display space radius to image space before passing to parent
    const imageWidth = frameCamera.intrinsics.width
    const radiusInImageSpace = dynamicRadiusRef.current * (imageWidth / frameDisplayWidth)
    
    console.log(`📸 Showing ${rankedCameras.length} camera thumbnails, radiusPx (display): ${dynamicRadiusRef.current.toFixed(1)}, radiusPx (image): ${radiusInImageSpace.toFixed(1)}, frameIndex: ${frameIndex}`)
    onShowThumbnails(rankedCameras, radiusInImageSpace, frameIndex)
  }, [imagePixelX, imagePixelY, frameCamera, cameras, points3D, offsetMap, frameIndex, onShowThumbnails, frameDisplayWidth])

  // Handle second tap on circle - show thumbnails only
  const handleClick = useCallback(() => {
    if (previewMode && onActivate) {
      console.log('🎯 [TapCameraIndicator] Preview mode tap - activating full controls')
      onActivate()
      return
    }
    console.log('🎯 [TapCameraIndicator] Tap on circle - showing thumbnails')
    
    // Circle tap shows thumbnails (badge tap triggers playback)
    showThumbnails()
  }, [showThumbnails, previewMode, onActivate])
  
  // Dynamic radius state (initialized from calculated radius)
  const [dynamicRadiusPx, setDynamicRadiusPx] = useState<number>(() => {
    // Initialize with calculated radius to avoid single-frame flash of 0px on remount
    const iw = frameCamera.intrinsics.width
    if (scaleData?.circleRadiusPx && frameDisplayWidth) {
      return scaleData.circleRadiusPx * (frameDisplayWidth / iw)
    }
    if (radiusPxImageSpace && frameDisplayWidth) {
      return radiusPxImageSpace * (frameDisplayWidth / iw)
    }
    return frameDisplayWidth ? frameDisplayWidth * 0.10 : 30
  })
  
  // Drag state for radius adjustment
  const [isDraggingRadius, setIsDraggingRadius] = useState<boolean>(false)
  
  // Pan offset state for moving entire indicator
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  
  // Track pan start position and center
  const panStartRef = useRef<{
    x: number
    y: number
    centerX: number
    centerY: number
    touchOffsetX?: number
    touchOffsetY?: number
    mouseOffsetX?: number
    mouseOffsetY?: number
    rect?: { left: number; top: number; right: number; bottom: number }
  } | null>(null)
  
  // Store handler references to clean up listeners properly
  const dragHandlersRef = useRef<{
    handleMouseMove: ((e: MouseEvent) => void) | null
    handleMouseUp: (() => void) | null
    handleTouchMove: ((e: TouchEvent) => void) | null
    handleTouchEnd: (() => void) | null
  }>({
    handleMouseMove: null,
    handleMouseUp: null,
    handleTouchMove: null,
    handleTouchEnd: null
  })
  
  // Ref for disk element to attach non-passive touch listeners
  const diskRef = useRef<HTMLDivElement>(null)
  
  // Track current radius in ref to avoid stale closure values during drag
  const dynamicRadiusRef = useRef<number>(0)
  
  // Track current zoom scale in ref so event handlers always use latest value
  const zoomScaleRef = useRef<number>(zoomScale)
  
  // Convert image space radius to display space radius
  // Priority: Use scaleData.circleRadiusPx if available (3D sphere projection per-camera)
  // Otherwise: Scale radiusPxImageSpace from image space to display space
  // Fallback: 10% of frame width or 30px
  const imageWidth = frameCamera.intrinsics.width
  const circleRadiusPx = scaleData?.circleRadiusPx 
    ? scaleData.circleRadiusPx * (frameDisplayWidth / imageWidth)  // Scale 3D-projected radius to display
    : (radiusPxImageSpace && frameDisplayWidth 
      ? radiusPxImageSpace * (frameDisplayWidth / imageWidth)      // Scale 2D radius to display
      : (frameDisplayWidth ? frameDisplayWidth * 0.10 : 30))        // Fallback
  
  console.log(`[🔵 Circle Radius Calc] scaleData: ${scaleData ? `circleRadiusPx=${scaleData.circleRadiusPx.toFixed(1)}px (3D)` : 'none'}, radiusPxImageSpace: ${radiusPxImageSpace?.toFixed(1) || 'none'}, imageWidth: ${imageWidth}px, frameDisplayWidth: ${frameDisplayWidth?.toFixed(1) || 'none'}px → circleRadiusPx: ${circleRadiusPx.toFixed(1)}px`)
  
  // Initialize dynamic radius on mount and when circleRadiusPx changes
  useEffect(() => {
    setDynamicRadiusPx(circleRadiusPx)
    dynamicRadiusRef.current = circleRadiusPx
    console.log('[Knob] Initialized dynamicRadiusPx to: ' + circleRadiusPx.toFixed(1) + 'px')
  }, [circleRadiusPx])
  
  // Update thumbnails when radius changes (only if thumbnails are already visible)
  useEffect(() => {
    // Skip if we haven't shown thumbnails yet (no cameras ranked)
    if (!frameCamera || dynamicRadiusPx === 0) return
    
    // Get frame element rect for camera ranking
    const frameElement = document.querySelector('.new-mode-frame-image')
    if (!frameElement) return
    
    const rect = frameElement.getBoundingClientRect()
    
    // 🔧 Use immutable frame-relative tap coordinates for camera discovery (same as handleClick)
    const lookupX = frameRelativeTapX
    const lookupY = frameRelativeTapY
    
    // Generate ranked cameras with current radius
    const rankedCameras = getRankedNearbyCameras(
      { x: lookupX, y: lookupY },
      rect,
      frameCamera,
      cameras,
      points3D,
      offsetMap,
      frameIndex,
      5 // Max 5 thumbnails
    )
    
    // Only update if we have cameras (thumbnails are visible)
    if (rankedCameras.length > 0) {
      console.log(`🔄 [Radius Update] Updating thumbnails with new radius: ${dynamicRadiusPx.toFixed(1)}px`)
      onShowThumbnails(rankedCameras, dynamicRadiusPx, frameIndex)
    }
  }, [dynamicRadiusPx, frameRelativeTapX, frameRelativeTapY, frameCamera, frameIndex, cameras, points3D, offsetMap, onShowThumbnails])
  
  // Update zoom scale ref whenever prop changes
  useEffect(() => {
    zoomScaleRef.current = zoomScale
  }, [zoomScale])
  
  // Attach native touchstart listener with passive:false for disk panning
  useEffect(() => {
    const diskElement = diskRef.current
    if (!diskElement) return
    
    const handleNativeTouchStart = (e: TouchEvent) => {
      if (isDraggingRadius) return
      if (e.touches.length === 0) return
      
      // Ignore touches that started on the knob - let knob handler handle it
      const target = e.target as HTMLElement
      if (target.hasAttribute('data-radius-knob-touch-area') || target.hasAttribute('data-radius-knob-visual')) {
        console.log('🔵 [PAN START] Ignoring - touch started on knob')
        return
      }
      
      e.preventDefault()  // Works because passive: false
      e.stopPropagation()
      
      const touch = e.touches[0]
      const currentCenterX = position.x + panOffset.x
      const currentCenterY = position.y + panOffset.y
      
      // Get the frame container's position to convert touch from viewport to frame-relative coords
      const rect = diskElement.parentElement?.getBoundingClientRect()
      if (!rect) return
      
      // Store the rect for consistent coordinate conversion throughout the drag
      const storedRect = { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom }
      
      // Convert touch from viewport coordinates to frame-relative display coordinates
      const touchDisplayX = touch.clientX - storedRect.left
      const touchDisplayY = touch.clientY - storedRect.top
      
      // Store initial offset from touch point to center (both in display coordinates)
      const touchOffsetX = currentCenterX - touchDisplayX
      const touchOffsetY = currentCenterY - touchDisplayY
      
      panStartRef.current = {
        x: touchDisplayX,
        y: touchDisplayY,
        centerX: currentCenterX,
        centerY: currentCenterY,
        touchOffsetX,
        touchOffsetY,
        rect: storedRect  // Store rect for reuse
      }
      
      console.log('🔵 [PAN START]')
      console.log('   Touch viewport:', touch.clientX.toFixed(1), touch.clientY.toFixed(1))
      console.log('   Rect (left, top):', storedRect.left.toFixed(1), storedRect.top.toFixed(1))
      console.log('   Touch display:', touchDisplayX.toFixed(1), touchDisplayY.toFixed(1))
      console.log('   position.x/y:', position.x.toFixed(1), position.y.toFixed(1))
      console.log('   panOffset.x/y:', panOffset.x.toFixed(1), panOffset.y.toFixed(1))
      console.log('   currentCenter:', currentCenterX.toFixed(1), currentCenterY.toFixed(1))
      console.log('   Calculated offset:', touchOffsetX.toFixed(1), touchOffsetY.toFixed(1))
      console.log('   Zoom scale:', zoomScaleRef.current.toFixed(2))
      document.documentElement.setAttribute('data-knob-dragging', 'true')
      
      const handleTouchMove = (event: TouchEvent) => {
        if (!panStartRef.current || event.touches.length === 0) return
        const touch = event.touches[0]
        
        // Use stored rect from pan start for consistent coordinate conversion
        const touchDisplayX = touch.clientX - panStartRef.current.rect.left
        const touchDisplayY = touch.clientY - panStartRef.current.rect.top
        
        // Calculate where center should be based on current touch + initial offset (both in display coordinates)
        const newCenterX = touchDisplayX + panStartRef.current.touchOffsetX
        const newCenterY = touchDisplayY + panStartRef.current.touchOffsetY
        // Pan offset is the delta from original center, divided by zoom scale so indicator moves at same speed as finger
        const deltaX = (newCenterX - panStartRef.current.centerX) / zoomScaleRef.current
        const deltaY = (newCenterY - panStartRef.current.centerY) / zoomScaleRef.current
        setPanOffset({ x: deltaX, y: deltaY })
        
        // Log every 5th move event to avoid spam
        if (!window._panMoveCount) window._panMoveCount = 0
        window._panMoveCount++
        if (window._panMoveCount % 5 === 0) {
          // Calculate center in viewport coordinates to show actual visual distance
          const centerViewportX = newCenterX + panStartRef.current.rect.left
          const centerViewportY = newCenterY + panStartRef.current.rect.top
          const visualDistanceX = centerViewportX - touch.clientX
          const visualDistanceY = centerViewportY - touch.clientY
          const visualDistance = Math.sqrt(visualDistanceX * visualDistanceX + visualDistanceY * visualDistanceY)
          
          console.log('🟢 [PAN MOVE]')
          console.log('   Touch viewport:', touch.clientX.toFixed(1), touch.clientY.toFixed(1))
          console.log('   Touch display:', touchDisplayX.toFixed(1), touchDisplayY.toFixed(1))
          console.log('   Stored offset:', panStartRef.current.touchOffsetX.toFixed(1), panStartRef.current.touchOffsetY.toFixed(1))
          console.log('   New center (display):', newCenterX.toFixed(1), newCenterY.toFixed(1))
          console.log('   New center (viewport):', centerViewportX.toFixed(1), centerViewportY.toFixed(1))
          console.log('   Visual distance from touch:', visualDistance.toFixed(1), 'px')
          console.log('   Delta (panOffset):', deltaX.toFixed(1), deltaY.toFixed(1))
        }
      }
      
      const handleTouchEnd = (event: TouchEvent) => {
        if (!panStartRef.current) return
        
        const touch = event.changedTouches[0]
        const touchDisplayX = touch.clientX - panStartRef.current.rect.left
        const touchDisplayY = touch.clientY - panStartRef.current.rect.top
        
        // Calculate movement distance to distinguish tap from drag
        const moveDistance = Math.sqrt(
          Math.pow(touchDisplayX - panStartRef.current.x, 2) + 
          Math.pow(touchDisplayY - panStartRef.current.y, 2)
        )
        
        // Strict tap threshold: < 2px movement = tap, >= 2px = drag
        if (moveDistance < 2) {
          console.log('🎯 [SECOND TAP] Detected tap on indicator (movement:', moveDistance.toFixed(1), 'px)')
          handleClick()
          
          // Clean up without calling onPanComplete
          setPanOffset({ x: 0, y: 0 })
          panStartRef.current = null
          document.documentElement.removeAttribute('data-knob-dragging')
          document.removeEventListener('touchmove', handleTouchMove)
          document.removeEventListener('touchend', handleTouchEnd)
          return
        }
        
        // Drag: complete pan normally
        console.log('🔴 [PAN END] Movement:', moveDistance.toFixed(1), 'px - completing pan')
        const finalX = touchDisplayX + panStartRef.current.touchOffsetX
        const finalY = touchDisplayY + panStartRef.current.touchOffsetY
        
        console.log('🔴 [PAN END]')
        console.log('   Touch viewport:', touch.clientX.toFixed(1), touch.clientY.toFixed(1))
        console.log('   Touch display:', touchDisplayX.toFixed(1), touchDisplayY.toFixed(1))
        console.log('   Stored offset:', panStartRef.current.touchOffsetX.toFixed(1), panStartRef.current.touchOffsetY.toFixed(1))
        console.log('   Final center (scaled display):', finalX.toFixed(1), finalY.toFixed(1))
        
        // Calculate final unscaled display position - the actual delta from pan start
        const finalDeltaX = (finalX - panStartRef.current.centerX) / zoomScaleRef.current
        const finalDeltaY = (finalY - panStartRef.current.centerY) / zoomScaleRef.current
        const finalUnscaledX = position.x + finalDeltaX
        const finalUnscaledY = position.y + finalDeltaY
        console.log('   Pan start center (scaled):', panStartRef.current.centerX.toFixed(1), panStartRef.current.centerY.toFixed(1))
        console.log('   Final delta (unscaled):', finalDeltaX.toFixed(1), finalDeltaY.toFixed(1))
        console.log('   Final center (unscaled display - component position):', finalUnscaledX.toFixed(1), finalUnscaledY.toFixed(1))
        
        // Call parent callback BEFORE reset (convert display coords back to screen/viewport coords)
        // rect.left/top already includes zoom transform, so just add the offset
        if (onPanComplete) {
          const screenX = finalX + panStartRef.current.rect.left
          const screenY = finalY + panStartRef.current.rect.top
          console.log('   Final center (screen):', screenX.toFixed(1), screenY.toFixed(1))
          console.log('   Calling onPanComplete with screen coords')
          window._panMoveCount = 0
          onPanComplete(screenX, screenY, finalUnscaledX, finalUnscaledY)
        }
        
        setPanOffset({ x: 0, y: 0 })
        panStartRef.current = null
        document.documentElement.removeAttribute('data-knob-dragging')
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
      
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleTouchEnd)
    }
    
    diskElement.addEventListener('touchstart', handleNativeTouchStart, { passive: false })
    
    return () => {
      diskElement.removeEventListener('touchstart', handleNativeTouchStart)
    }
  }, [isDraggingRadius, onPanComplete])

  // Handle knob drag start (mouse and touch)
  const handleKnobMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Get frame rect to convert viewport coords to display coords
    const diskElement = diskRef.current
    if (!diskElement) return
    const frameRect = diskElement.parentElement?.getBoundingClientRect()
    if (!frameRect) return
    
    // 🔧 CRITICAL: Use IMAGE PIXEL coordinates for knob center (zoom-independent)
    // imagePixelX/Y are already in image space (0-450, 0-800)
    const centerImagePixelX = imagePixelX
    const centerImagePixelY = imagePixelY
    
    // Convert mouse viewport coords to display coords, then to image space
    const mouseDisplayX = e.clientX - frameRect.left
    const mouseDisplayY = e.clientY - frameRect.top
    
    // Convert display to image space
    const mouseImagePixelX = (mouseDisplayX / frameRect.width) * frameCamera.intrinsics.width
    const mouseImagePixelY = (mouseDisplayY / frameRect.height) * frameCamera.intrinsics.height
    
    // Store starting radius value to detect if it changed during drag
    const startRadius = dynamicRadiusRef.current
    
    // Calculate initial radius in IMAGE SPACE (zoom-independent!)
    const initialDeltaX = mouseImagePixelX - centerImagePixelX
    const initialDeltaY = mouseImagePixelY - centerImagePixelY
    const initialRadius = Math.sqrt(initialDeltaX * initialDeltaX + initialDeltaY * initialDeltaY)
    
    console.log('[Knob Drag] Starting mouse drag (IMAGE SPACE):')
    console.log('   Mouse viewport:', e.clientX, e.clientY)
    console.log('   Mouse display:', mouseDisplayX.toFixed(1), mouseDisplayY.toFixed(1))
    console.log('   Mouse image pixels:', mouseImagePixelX.toFixed(1), mouseImagePixelY.toFixed(1))
    console.log('   Center image pixels:', centerImagePixelX.toFixed(1), centerImagePixelY.toFixed(1))
    console.log('   Initial radius (image space):', initialRadius.toFixed(1) + 'px')
    console.log('   Current dynamicRadiusPx:', dynamicRadiusRef.current.toFixed(1) + 'px')
    
    // Mark that knob dragging is active (so Hammer.js can check and ignore gestures)
    document.documentElement.setAttribute('data-knob-dragging', 'true')
    onDraggingStatusChange?.(true)
    setIsDraggingRadius(true)
    
    // Create mouse move handler
    const handleMouseMove = (event: MouseEvent) => {
      // Convert viewport to display coords
      const mouseDisplayX = event.clientX - frameRect.left
      const mouseDisplayY = event.clientY - frameRect.top
      
      // Convert display coords to image space
      const mouseImagePixelX = (mouseDisplayX / frameRect.width) * frameCamera.intrinsics.width
      const mouseImagePixelY = (mouseDisplayY / frameRect.height) * frameCamera.intrinsics.height
      
      // Distance in IMAGE SPACE (zoom-independent!)
      const deltaX = mouseImagePixelX - centerImagePixelX
      const deltaY = mouseImagePixelY - centerImagePixelY
      const newRadius = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      setDynamicRadiusPx(newRadius)
      dynamicRadiusRef.current = newRadius
      console.log('[Knob Drag] Moving - image pixels:', mouseImagePixelX.toFixed(1), mouseImagePixelY.toFixed(1), '-> radius (image space):', newRadius.toFixed(1) + 'px')
    }
    
    // Create mouse up handler
    const handleMouseUp = (event: MouseEvent) => {
      console.log('🟢 [KNOB MOUSE UP] Firing at viewport:', event.clientX.toFixed(1), event.clientY.toFixed(1))
      console.log('[Knob Drag] Ending mouse drag, final radius:', dynamicRadiusRef.current.toFixed(1) + 'px')
      document.documentElement.removeAttribute('data-knob-dragging')
      
      // Add click handlers to block both native click AND Hammer.js synthetic events
      let clickCount = 0
      const blockClick = (e: MouseEvent) => {
        clickCount++
        console.log(`🚫 [Click Blocker] Blocked click #${clickCount} after knob drag`)
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        // Keep blocking for up to 2 clicks or 300ms
        if (clickCount >= 2) {
          document.removeEventListener('click', blockClick, true)
        }
      }
      document.addEventListener('click', blockClick, true) // Capture phase to intercept first
      
      // Remove blocker after 300ms as safety cleanup
      setTimeout(() => {
        document.removeEventListener('click', blockClick, true)
        console.log('✅ [Click Blocker] Cleanup completed')
      }, 300)
      
      setIsDraggingRadius(false)
      onDraggingStatusChange?.(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      dragHandlersRef.current.handleMouseMove = null
      dragHandlersRef.current.handleMouseUp = null
      
      // Update thumbnail radius without regenerating camera list
      const finalRadius = dynamicRadiusRef.current
      const radiusChanged = Math.abs(finalRadius - startRadius) > 1 // 1px threshold
      if (radiusChanged) {
        console.log('[Knob Drag] Mouse drag ended, radius changed from', startRadius.toFixed(1), 'to', finalRadius.toFixed(1), '- updating thumbnail views')
        // Convert display space radius to image space radius (JPG pixels)
        const radiusInImageSpace = finalRadius * (imageWidth / frameDisplayWidth)
        console.log('[Knob Drag] Converting radius: display', finalRadius.toFixed(1), 'px → image space', radiusInImageSpace.toFixed(1), 'px')
        onUpdateRadius?.(radiusInImageSpace)
      } else {
        console.log('[Knob Drag] Mouse drag ended, radius unchanged at', finalRadius.toFixed(1), 'px')
      }
    }
    
    // Store references for cleanup
    dragHandlersRef.current.handleMouseMove = handleMouseMove
    dragHandlersRef.current.handleMouseUp = handleMouseUp
    
    // Add document-level listeners for drag continuation
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [imagePixelX, imagePixelY, frameCamera, onDraggingStatusChange])

  const handleKnobTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (e.touches.length === 0) return
    const touch = e.touches[0]
    
    // Get frame rect to convert viewport coords to display coords
    const diskElement = diskRef.current
    if (!diskElement) return
    const frameRect = diskElement.parentElement?.getBoundingClientRect()
    if (!frameRect) return
    
    // 🔧 CRITICAL: Use IMAGE PIXEL coordinates for knob center (zoom-independent)
    const centerImagePixelX = imagePixelX
    const centerImagePixelY = imagePixelY
    
    // Convert touch viewport coords to display coords, then to image space
    const touchDisplayX = touch.clientX - frameRect.left
    const touchDisplayY = touch.clientY - frameRect.top
    
    // Convert display to image space
    const touchImagePixelX = (touchDisplayX / frameRect.width) * frameCamera.intrinsics.width
    const touchImagePixelY = (touchDisplayY / frameRect.height) * frameCamera.intrinsics.height
    
    // Store starting radius value to detect if it changed during drag
    const startRadius = dynamicRadiusRef.current
    
    console.log('[Knob Drag] Starting touch drag (IMAGE SPACE):')
    console.log('   Touch viewport:', touch.clientX, touch.clientY)
    console.log('   Touch display:', touchDisplayX.toFixed(1), touchDisplayY.toFixed(1))
    console.log('   Touch image pixels:', touchImagePixelX.toFixed(1), touchImagePixelY.toFixed(1))
    console.log('   Center image pixels:', centerImagePixelX.toFixed(1), centerImagePixelY.toFixed(1))
    
    // Mark that knob dragging is active (so Hammer.js can check and ignore gestures)
    document.documentElement.setAttribute('data-knob-dragging', 'true')
    onDraggingStatusChange?.(true)
    setIsDraggingRadius(true)
    
    // Create touch move handler
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 0) return
      const touch = event.touches[0]
      
      // Convert viewport to display coords
      const touchDisplayX = touch.clientX - frameRect.left
      const touchDisplayY = touch.clientY - frameRect.top
      
      // Convert display coords to image space
      const touchImagePixelX = (touchDisplayX / frameRect.width) * frameCamera.intrinsics.width
      const touchImagePixelY = (touchDisplayY / frameRect.height) * frameCamera.intrinsics.height
      
      // Distance in IMAGE SPACE (zoom-independent!)
      const deltaX = touchImagePixelX - centerImagePixelX
      const deltaY = touchImagePixelY - centerImagePixelY
      const newRadius = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      setDynamicRadiusPx(newRadius)
      dynamicRadiusRef.current = newRadius
      // console.log('[Knob Drag] Touch moving - image pixels:', touchImagePixelX.toFixed(1), touchImagePixelY.toFixed(1), '-> radius (image space):', newRadius.toFixed(1) + 'px')
    }
    
    // Create touch end handler
    const handleTouchEnd = () => {
      console.log('[Knob Drag] Ending touch drag, final radius:', dynamicRadiusRef.current.toFixed(1) + 'px')
      
      // Keep data-knob-dragging attribute for 350ms
      // Hammer.js singletap waits 300ms for potential doubletap before firing
      // We need to keep the flag set longer than that interval
      setTimeout(() => {
        console.log('[Knob Drag] Removing data-knob-dragging attribute after 350ms')
        document.documentElement.removeAttribute('data-knob-dragging')
      }, 350)
      
      setIsDraggingRadius(false)
      onDraggingStatusChange?.(false)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      dragHandlersRef.current.handleTouchMove = null
      dragHandlersRef.current.handleTouchEnd = null
      
      // Only regenerate thumbnails if radius actually changed (not just a tap)
      const finalRadius = dynamicRadiusRef.current
      const radiusChanged = Math.abs(finalRadius - startRadius) > 1 // 1px threshold
      if (radiusChanged) {
        console.log('[Knob Drag] Touch drag ended, radius changed from', startRadius.toFixed(1), 'to', finalRadius.toFixed(1), '- updating thumbnail views')
        // Convert display space radius to image space radius (JPG pixels)
        const radiusInImageSpace = finalRadius * (imageWidth / frameDisplayWidth)
        console.log('[Knob Drag] Converting radius: display', finalRadius.toFixed(1), 'px → image space', radiusInImageSpace.toFixed(1), 'px')
        onUpdateRadius?.(radiusInImageSpace)
      } else {
        console.log('[Knob Drag] Touch drag ended, radius unchanged at', finalRadius.toFixed(1), 'px')
      }
    }
    
    // Store references for cleanup
    dragHandlersRef.current.handleTouchMove = handleTouchMove
    dragHandlersRef.current.handleTouchEnd = handleTouchEnd
    
    // Add document-level listeners for drag continuation
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)
  }, [imagePixelX, imagePixelY, frameCamera, onDraggingStatusChange])

  // Cleanup drag listeners on unmount
  useEffect(() => {
    return () => {
      if (dragHandlersRef.current.handleMouseMove) {
        document.removeEventListener('mousemove', dragHandlersRef.current.handleMouseMove)
      }
      if (dragHandlersRef.current.handleMouseUp) {
        document.removeEventListener('mouseup', dragHandlersRef.current.handleMouseUp)
      }
      if (dragHandlersRef.current.handleTouchMove) {
        document.removeEventListener('touchmove', dragHandlersRef.current.handleTouchMove)
      }
      if (dragHandlersRef.current.handleTouchEnd) {
        document.removeEventListener('touchend', dragHandlersRef.current.handleTouchEnd)
      }
    }
  }, [])

  // Apply pan offset to current center position
  const currentCenterX = position.x + panOffset.x
  const currentCenterY = position.y + panOffset.y

  // Calculate bottom center position for camera badge (-90° bottom)
  const badgeDistance = dynamicRadiusPx + 15 // Distance from center to badge edge (closer to circle)
  const badgeAngle = -90 * (Math.PI / 180) // -90 degrees (bottom center)
  const badgeX = currentCenterX + badgeDistance * Math.cos(badgeAngle)
  const badgeY = currentCenterY - badgeDistance * Math.sin(badgeAngle)

  // Calculate knob position at circle edge (45° top-right)
  const knobAngle = 45 * (Math.PI / 180) // 45 degrees in radians
  const knobDistance = dynamicRadiusPx  // At edge of circle
  const knobX = currentCenterX + knobDistance * Math.cos(knobAngle)
  const knobY = currentCenterY - knobDistance * Math.sin(knobAngle)

  // Calculate close button position at circle edge (180° left center)
  const closeButtonAngle = 180 * (Math.PI / 180) // 180 degrees (left center)
  const closeButtonDistance = dynamicRadiusPx + 40 // Between blue circle and disk edge
  const closeButtonX = currentCenterX + closeButtonDistance * Math.cos(closeButtonAngle)
  const closeButtonY = currentCenterY - closeButtonDistance * Math.sin(closeButtonAngle)

  // Calculate WhatsApp button position (200° bottom-left, away from camera badge)
  const whatsappButtonAngle = 200 * (Math.PI / 180) // 200 degrees (bottom-left, between close and badge)
  const whatsappButtonDistance = dynamicRadiusPx + 40
  const whatsappButtonX = currentCenterX + whatsappButtonDistance * Math.cos(whatsappButtonAngle)
  const whatsappButtonY = currentCenterY - whatsappButtonDistance * Math.sin(whatsappButtonAngle)

  // Disk event handlers - block parent gestures when touching anywhere in disk
  const handleDiskMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Skip if already dragging radius
    if (isDraggingRadius) return
    
    // Ignore mouse down that started on the knob - let knob handler handle it
    const target = e.target as HTMLElement
    if (target.hasAttribute('data-radius-knob-touch-area') || target.hasAttribute('data-radius-knob-visual')) {
      console.log('🔵 [MOUSE PAN START] Ignoring - mouse down on knob')
      return
    }
    
    const currentCenterX = position.x + panOffset.x
    const currentCenterY = position.y + panOffset.y
    
    // Get the frame container's position to convert mouse from viewport to frame-relative coords
    const diskElement = diskRef.current
    if (!diskElement) return
    const rect = diskElement.parentElement?.getBoundingClientRect()
    if (!rect) return
    
    // Store the rect for consistent coordinate conversion throughout the drag
    const storedRect = { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom }
    
    // Convert mouse from viewport coordinates to frame-relative display coordinates
    const mouseDisplayX = e.clientX - storedRect.left
    const mouseDisplayY = e.clientY - storedRect.top
    
    // Store initial offset from mouse point to center (both in display coordinates)
    const mouseOffsetX = currentCenterX - mouseDisplayX
    const mouseOffsetY = currentCenterY - mouseDisplayY
    
    // Start pan tracking
    panStartRef.current = {
      x: mouseDisplayX,
      y: mouseDisplayY,
      centerX: currentCenterX,
      centerY: currentCenterY,
      mouseOffsetX,
      mouseOffsetY,
      rect: storedRect
    }
    
    console.log('🔵 [MOUSE PAN START]')
    console.log('   Mouse viewport:', e.clientX.toFixed(1), e.clientY.toFixed(1))
    console.log('   Rect (left, top):', storedRect.left.toFixed(1), storedRect.top.toFixed(1))
    console.log('   Mouse display:', mouseDisplayX.toFixed(1), mouseDisplayY.toFixed(1))
    console.log('   position.x/y:', position.x.toFixed(1), position.y.toFixed(1))
    console.log('   panOffset.x/y:', panOffset.x.toFixed(1), panOffset.y.toFixed(1))
    console.log('   currentCenter:', currentCenterX.toFixed(1), currentCenterY.toFixed(1))
    console.log('   Calculated offset:', mouseOffsetX.toFixed(1), mouseOffsetY.toFixed(1))
    console.log('   Zoom scale:', zoomScaleRef.current.toFixed(2))
    document.documentElement.setAttribute('data-knob-dragging', 'true')
    
    // Create mouse move handler for panning
    const handleMouseMove = (event: MouseEvent) => {
      if (!panStartRef.current || !panStartRef.current.rect) return
      
      // Use stored rect from pan start for consistent coordinate conversion
      const mouseDisplayX = event.clientX - panStartRef.current.rect.left
      const mouseDisplayY = event.clientY - panStartRef.current.rect.top
      
      // Calculate where center should be based on current mouse + initial offset (both in display coordinates)
      const newCenterX = mouseDisplayX + panStartRef.current.mouseOffsetX!
      const newCenterY = mouseDisplayY + panStartRef.current.mouseOffsetY!
      
      // Pan offset is the delta from original center, divided by zoom scale so indicator moves at same speed as cursor
      const deltaX = (newCenterX - panStartRef.current.centerX) / zoomScaleRef.current
      const deltaY = (newCenterY - panStartRef.current.centerY) / zoomScaleRef.current
      setPanOffset({ x: deltaX, y: deltaY })
    }
    
    // Create mouse up handler for finalizing pan
    const handleMouseUp = (event: MouseEvent) => {

      if (!panStartRef.current || !panStartRef.current.rect) return
      
      // Use stored rect from pan start for consistent coordinate conversion
      const mouseDisplayX = event.clientX - panStartRef.current.rect.left
      const mouseDisplayY = event.clientY - panStartRef.current.rect.top
      const finalX = mouseDisplayX + panStartRef.current.mouseOffsetX!
      const finalY = mouseDisplayY + panStartRef.current.mouseOffsetY!
      
      // Calculate movement distance to distinguish tap from drag
      const moveDistance = Math.sqrt(
        Math.pow(mouseDisplayX - panStartRef.current.x, 2) + 
        Math.pow(mouseDisplayY - panStartRef.current.y, 2)
      )
      
      // Desktop click: < 2px movement = click, show thumbnails only
      if (moveDistance < 2) {
        console.log('🎯 [DESKTOP CLICK] Detected click on indicator - showing thumbnails')
        showThumbnails()
        
        // Clean up without calling onPanComplete
        setPanOffset({ x: 0, y: 0 })
        panStartRef.current = null
        document.documentElement.removeAttribute('data-knob-dragging')
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        return
      }
      
      console.log('🔴 [MOUSE PAN END]')
      console.log('   Mouse viewport:', event.clientX.toFixed(1), event.clientY.toFixed(1))
      console.log('   Mouse display:', mouseDisplayX.toFixed(1), mouseDisplayY.toFixed(1))
      console.log('   Stored offset:', panStartRef.current.mouseOffsetX!.toFixed(1), panStartRef.current.mouseOffsetY!.toFixed(1))
      console.log('   Final center (scaled display):', finalX.toFixed(1), finalY.toFixed(1))
      
      // Calculate final unscaled display position - the actual delta from pan start
      const finalDeltaX = (finalX - panStartRef.current.centerX) / zoomScaleRef.current
      const finalDeltaY = (finalY - panStartRef.current.centerY) / zoomScaleRef.current
      const finalUnscaledX = position.x + finalDeltaX
      const finalUnscaledY = position.y + finalDeltaY
      console.log('   Pan start center (scaled):', panStartRef.current.centerX.toFixed(1), panStartRef.current.centerY.toFixed(1))
      console.log('   Final delta (unscaled):', finalDeltaX.toFixed(1), finalDeltaY.toFixed(1))
      console.log('   Final center (unscaled display - component position):', finalUnscaledX.toFixed(1), finalUnscaledY.toFixed(1))
      
      // Call parent callback BEFORE reset (convert display coords back to screen/viewport coords)
      if (onPanComplete) {
        const screenX = finalX + panStartRef.current.rect.left
        const screenY = finalY + panStartRef.current.rect.top
        console.log('   Final center (screen):', screenX.toFixed(1), screenY.toFixed(1))
        console.log('   Calling onPanComplete with screen coords')
        onPanComplete(screenX, screenY, finalUnscaledX, finalUnscaledY)
      }
      
      setPanOffset({ x: 0, y: 0 })
      panStartRef.current = null
      document.documentElement.removeAttribute('data-knob-dragging')
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [isDraggingRadius, position, panOffset, onPanComplete])

  const handleDiskTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Skip if already dragging radius
    if (isDraggingRadius) return
    if (e.touches.length === 0) return
    
    const touch = e.touches[0]
    
    // Start pan tracking (use current center position, not original displayX/Y)
    panStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      centerX: currentCenterX,
      centerY: currentCenterY,
    }
    
    console.log('[Indicator Pan] Starting touch pan from:', touch.clientX, touch.clientY, 'current center:', currentCenterX.toFixed(1), currentCenterY.toFixed(1))
    document.documentElement.setAttribute('data-knob-dragging', 'true')
    
    // Create touch move handler for panning
    const handleTouchMove = (event: TouchEvent) => {
      if (!panStartRef.current || event.touches.length === 0) return
      const touch = event.touches[0]
      const deltaX = touch.clientX - panStartRef.current.x
      const deltaY = touch.clientY - panStartRef.current.y
      setPanOffset({ x: deltaX, y: deltaY })
      console.log('[Indicator Pan] Touch moving to:', touch.clientX, touch.clientY, '-> offset:', deltaX.toFixed(1), deltaY.toFixed(1))
    }
    
    // Create touch end handler for finalizing pan
    const handleTouchEnd = () => {
      if (!panStartRef.current) return
      console.log('[Indicator Pan] Ending touch pan')
      setPanOffset({ x: 0, y: 0 })
      panStartRef.current = null
      document.documentElement.removeAttribute('data-knob-dragging')
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)
  }, [isDraggingRadius, currentDisplayX, currentDisplayY, panOffset])

  const handleDiskTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Already handled in handleTouchEnd listener above
  }, [])

  const handleDiskClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDiskContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Master event blocker - prevents ALL events from propagating to parent for entire indicator area
  const handleMasterBlockerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Let disk pan handle it
    handleDiskMouseDown(e)
  }, [handleDiskMouseDown])

  const handleMasterBlockerTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Let disk pan handle it
    handleDiskTouchStart(e)
  }, [handleDiskTouchStart])

  const handleMasterBlockerTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleMasterBlockerClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Desktop: show thumbnails only (badge click triggers playback separately)
    showThumbnails()
  }, [showThumbnails])

  const handleMasterBlockerContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  return (
    <>
      {/* Master event blocker - transparent overlay covering entire indicator (circle + 30px buffer) */}
      {/* This blocks ALL mouse/touch/click events from propagating to parent */}
      <div
        data-tap-master-blocker
        onMouseDown={handleMasterBlockerMouseDown}
        onClick={handleMasterBlockerClick}
        onContextMenu={handleMasterBlockerContextMenu}
        onTouchStart={handleMasterBlockerTouchStart}
        onTouchEnd={handleMasterBlockerTouchEnd}
        style={{
          position: 'absolute',
          left: `${currentCenterX}px`,
          top: `${currentCenterY}px`,
          transform: 'translate(-50%, -50%)',
          width: `${(dynamicRadiusPx + 30) * 2}px`,
          height: `${(dynamicRadiusPx + 30) * 2}px`,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          pointerEvents: 'auto',
          zIndex: 9990,  // Below other elements but blocks parent
          cursor: 'auto',
        }}
      />

      {/* Transparent red disk - event isolation area (circle + 30px buffer) */}
      <div
        ref={diskRef}
        data-tap-disk-blocker
        onMouseDown={handleDiskMouseDown}
        onClick={handleDiskClick}
        onContextMenu={handleDiskContextMenu}
        style={{
          position: 'absolute',
          left: `${currentCenterX}px`,
          top: `${currentCenterY}px`,
          transform: 'translate(-50%, -50%)',
          width: `${(dynamicRadiusPx + 30) * 2}px`,
          height: `${(dynamicRadiusPx + 30) * 2}px`,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          pointerEvents: 'auto',
          zIndex: 9999,
          border: '2px solid rgba(255, 0, 0, 0.4)',
          cursor: panOffset.x !== 0 || panOffset.y !== 0 ? 'grabbing' : 'grab',
          transition: (panOffset.x === 0 && panOffset.y === 0) ? 'cursor 0.2s ease' : 'none',
        }}
      />

      {/* Blue circle overlay - shows radius based on camera distance */}
      <div
        data-tap-circle-overlay
        style={{
          position: 'absolute',
          left: `${currentCenterX}px`,
          top: `${currentCenterY}px`,
          transform: 'translate(-50%, -50%)',
          width: `${dynamicRadiusPx * 2}px`,
          height: `${dynamicRadiusPx * 2}px`,
          borderRadius: '50%',
          border: '2px solid rgba(100, 150, 255, 0.4)',
          backgroundColor: 'rgba(100, 150, 255, 0.1)',
          pointerEvents: 'none',
          zIndex: 9998,
          boxShadow: '0 0 15px rgba(100, 150, 255, 0.4)',
        }}
      />

      {/* Share buttons - WhatsApp + Share (mobile) or WhatsApp only (desktop) */}
      {!previewMode && (
        <ShareButtonsGroup
          circleX={imagePixelX}
          circleY={imagePixelY}
          circleRadius={dynamicRadiusPx * (imageWidth / (frameDisplayWidth || 1))}
          buttonX={whatsappButtonX}
          buttonY={whatsappButtonY}
          shopId={shopId}
          frameIndex={frameIndex}
        />
      )}

      {/* Camera badge - icon + count, 45° top-right tangent, clickable via master blocker */}
      {/* Close button - x icon, positioned at 180° left center on circle edge */}
      {!previewMode && onClose && (
        <div
          data-tap-close
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
          style={{
            position: 'absolute',
            left: `${closeButtonX}px`,
            top: `${closeButtonY}px`,
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            borderRadius: '50%',
            border: '2px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 0 12px rgba(255, 59, 48, 0.5)',
            pointerEvents: 'auto',
            cursor: 'pointer',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 59, 48, 0.3)'
            e.currentTarget.style.borderColor = 'rgba(255, 100, 100, 0.7)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)'
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(255, 255, 255, 0.9)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>
      )}

      {!previewMode && <div
        data-tap-badge
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          console.log('🎯 [BADGE CLICK] Showing thumbnails and triggering playback')
          
          // Check if tap data exists before showing thumbnails
          if (imagePixelX !== undefined && imagePixelY !== undefined) {
            showThumbnails()
            onCameraBadgeClick?.()
          } else {
            console.warn('⚠️ [BADGE CLICK] Cannot show thumbnails - missing tap coordinates')
          }
        }}
        style={{
          position: 'absolute',
          left: `${badgeX}px`,
          top: `${badgeY}px`,
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 0 12px rgba(100, 150, 255, 0.5)',
          pointerEvents: 'auto',
          cursor: onCameraBadgeClick ? 'pointer' : 'default',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          padding: '6px 8px',
          width: 'auto',
          height: 'auto',
        }}
      >
        {/* Camera count */}
        <span
          style={{
            fontSize: '16px',
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.95)',
            textShadow: '0 0 4px rgba(0, 0, 0, 0.8)',
          }}
        >
          {cameraCount}
        </span>
        {/* Camera surrounded by orbit circle */}
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(255, 255, 255, 0.9)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Orbit circle with arrow */}
          <circle cx="12" cy="12" r="10" opacity="0.7" strokeWidth="2.5" />
          {/* Arrowhead pointing clockwise (rightward at top of circle) */}
          <path d="M 12 2 L 9 4 M 12 2 L 9 0" strokeWidth="2.5" opacity="0.8" />
          
          {/* Camera body (smaller, centered) */}
          <path d="M16.5 14a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h1.5l0.5-1h3l0.5 1h1.5a1 1 0 0 1 1 1z" strokeWidth="1.2" />
          <circle cx="12" cy="12" r="1.5" strokeWidth="1.2" />
        </svg>
      
      </div>}

      {/* Draggable knob at circle edge (45° top-right) for radius adjustment */}
      {/* Invisible 50px touch area for mobile UX (larger hit target) */}
      {!previewMode && <div
        data-radius-knob-touch-area
        onMouseDown={handleKnobMouseDown}
        onTouchStart={handleKnobTouchStart}
        style={{
          position: 'absolute',
          left: `${knobX}px`,
          top: `${knobY}px`,
          transform: 'translate(-50%, -50%)',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          pointerEvents: 'auto',
          cursor: isDraggingRadius ? 'grabbing' : 'grab',
          zIndex: 10001,
          backgroundColor: isDraggingRadius ? 'rgba(200, 200, 200, 0.15)' : 'transparent',
          transition: isDraggingRadius ? 'none' : 'background-color 0.3s ease',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      />}

      {/* Visual knob indicator - SVG with two arrows showing radius adjustment */}
      {!previewMode && <svg
        data-radius-knob-visual
        style={{
          position: 'absolute',
          left: `${knobX}px`,
          top: `${knobY}px`,
          transform: 'translate(-50%, -50%) rotate(45deg)',
          width: '24px',
          height: '24px',
          pointerEvents: 'none',
          zIndex: 10002,
          filter: 'drop-shadow(0 0 4px rgba(200, 200, 200, 0.6))',
          transition: isDraggingRadius ? 'none' : 'filter 0.2s ease',
        }}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Outer arrow (expand radius) */}
        <path
          d="M12 0 L18 6 L16 6 L16 8 L8 8 L8 6 L6 6 Z"
          fill="rgba(200, 200, 200, 0.9)"
          stroke="rgba(255, 255, 255, 0.9)"
          strokeWidth="0.5"
        />
        {/* Inner arrow (shrink radius) */}
        <path
          d="M12 24 L6 18 L8 18 L8 16 L16 16 L16 18 L18 18 Z"
          fill="rgba(200, 200, 200, 0.9)"
          stroke="rgba(255, 255, 255, 0.9)"
          strokeWidth="0.5"
        />
      </svg>}

      {/* Overlay to block parent interactions during drag */}
      {isDraggingRadius && (
        <div
          data-drag-overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(128, 128, 128, 0.3)',
            zIndex: 9997,
            pointerEvents: 'auto',
            cursor: 'grabbing',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            touchAction: 'none',
          }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
          onMouseMove={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        />
      )}
    </>
  )
}